/// <reference path="../libs.ts" />
import { IPromise } from 'angular';
import * as _ from 'underscore';
import { CreatePersonalAccessTokenDialogCtrl } from '../controllers/CreatePersonalAccessTokenDialogCtrl';
import { FeatureFlags } from '../FeatureFlags';
import { ActionContext } from '../helpers/ActionContext';
import { IServerUserInfo } from '../models/IServerUserInfo';
import { PolicyNames } from '../models/user/validation/IPasswordPolicy';
import { LocalAuthConfiguration } from '../models/user/validation/LocalAuthConfiguration';
import { Module } from '../Module';
import { ServerUsers } from '../resources/ServerUsers';
import { CreatePersonalAccessTokenRequest, CreatePersonalAccessTokenResult } from '../ServerApi/requests/CreatePersonalAccessTokenRequest';
import { GetLocalAuthConfigurationRequest } from '../ServerApi/requests/GetLocalAuthConfigurationRequest';
import { IConnectedDevice } from '../ServerApi/types/IConnectedDevice';
import { IPersonalAccessToken } from '../ServerApi/types/IPersonalAccessToken';
import { DeletePersonalAccessTokenAction } from './DeletePersonalAccessTokenAction';
import { ModalService, ModalSize } from './ModalService';
import { ServerService } from './ServerService';

export interface PersonalAccessTokensScope extends ng.IScope {
    // Create New Token
    personalAccessTokenName: string;
    createNewPersonalAccessToken: () => void;
    isCreateNewPersonalAccessTokenDisabled: () => boolean;
    canCreatePersonalAccessTokens: boolean;

    // List All Tokens
    connectedPersonalAccessTokens: IPersonalAccessToken[];
    deletePersonalAccessToken(token: IPersonalAccessToken, $event: JQueryEventObject): void;

    connectedDevices: IConnectedDevice[];
}

export class PersonalAccessTokensService {

  public static DEVICE_NAME_FOR_TOKENS = 'PERSONAL_ACCESS_TOKEN';
  private serverService: ServerService;
  private serverUsers: ServerUsers;
  private modalService: ModalService;
  private $translate: ng.translate.ITranslateService;
  private deletePersonalAccessTokenAction: DeletePersonalAccessTokenAction;

  public static $inject = [
    'ServerService',
    'ServerUsers',
    'ModalService',
    '$translate',
    'DeletePersonalAccessTokenAction',
  ];

  constructor(
      serverService: ServerService,
      serverUsers: ServerUsers,
      modalService: ModalService,
      $translate: ng.translate.ITranslateService,
      deletePersonalAccessTokenAction: DeletePersonalAccessTokenAction,
      ) {
    this.serverService = serverService;
    this.serverUsers = serverUsers;
    this.modalService = modalService;
    this.$translate = $translate;
    this.deletePersonalAccessTokenAction = deletePersonalAccessTokenAction;
  }

  public loadTokensIntoScope(scope: PersonalAccessTokensScope, serverUser: IServerUserInfo): () => void {
    // If the feature is disabled, we leave the scope as is.
    if (!FeatureFlags.personalAccessTokensEnabled) {
        return () => {}; // Empty callback
     }

    const refreshTokensAndDevices = () => {
      // Refresh Personal Access Tokens
      this.serverUsers.getPersonalAccessTokens(serverUser.id)
        .then((tokens:IPersonalAccessToken[]) => scope.connectedPersonalAccessTokens = tokens);

      // Replacing the custom tag for a localized readable label on the devices
      if (scope.connectedDevices) {
        scope.connectedDevices.filter(x => x.displayName === PersonalAccessTokensService.DEVICE_NAME_FOR_TOKENS)
        .forEach(x => x.displayName = this.$translate.instant(PersonalAccessTokensService.DEVICE_NAME_FOR_TOKENS));
      }
    };
    return refreshTokensAndDevices;
  }

  public loadActionsIntoScope(scope: PersonalAccessTokensScope, serverUser: IServerUserInfo, isSessionUserSelf: boolean,
    refreshDevicesAndTokens: () => void) {
    if (!FeatureFlags.personalAccessTokensEnabled) {
        return;
    }

    // Create tokens should only appear on the personal settings page, since we are always creating tokens for oneself.
    scope.canCreatePersonalAccessTokens = isSessionUserSelf;
    scope.createNewPersonalAccessToken = () => this.createNewPersonalAccessToken(scope, refreshDevicesAndTokens);

    scope.isCreateNewPersonalAccessTokenDisabled = () => {
      const name = scope.personalAccessTokenName;
      const tokens = scope.connectedPersonalAccessTokens;
      return !name || name.length === 0 || (tokens && tokens.some(x => x.clientId === name));
    };

    scope.deletePersonalAccessToken = (token:IPersonalAccessToken, $event:JQueryEventObject) =>
      this.deletePersonalAccessTokenAction.execute(token, serverUser.id, ActionContext.fromEvent($event))
        .then(refreshDevicesAndTokens);
  }

  private createNewPersonalAccessToken = (scope: PersonalAccessTokensScope, refreshDevicesAndTokens: () => void) => {
    this.serverService.sendRequest(new CreatePersonalAccessTokenRequest({
        clientId: scope.personalAccessTokenName
    })).then((result: CreatePersonalAccessTokenResult) => {
        const dialogInstance = this.modalService.open({
            scope: scope.$parent, // needed so that scope events can go through
            templateUrl: 'createPersonalAccessTokenDialog.html',
            size: ModalSize.fitToContent,
            controller: CreatePersonalAccessTokenDialogCtrl,
            resolve: {
                token: () => result,
            },
        });
        dialogInstance.result.then(() => {
            scope.personalAccessTokenName = '';
        });
        dialogInstance.result.finally(refreshDevicesAndTokens);
    });
  }
}

Module.instance().service('PersonalAccessTokensService', PersonalAccessTokensService);
